import React, { useState, useEffect, useRef } from "react";
import { validateTextField, validateEmail } from "../../utils/formFieldsValidator";
import { trackRegistrationField } from "../../utils/registrationTracking";
import { getStepOneStyles } from '../../styles/AutoRegisterFormStyles';

const StepOne = ({ formData, setFormData, isFormValid, content, onValidationChange, isMobileView }) => {
  const { name = "", surname = "", email = "", companyName = "", acceptedTerms = false } = formData;
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [hoveredLink, setHoveredLink] = useState(null);
  const onValidationChangeRef = useRef(onValidationChange);
  const trackTimersRef = useRef({});
  const lastTrackedRef = useRef({});
  const styles = getStepOneStyles();

  // Update ref when prop changes
  useEffect(() => {
    onValidationChangeRef.current = onValidationChange;
  }, [onValidationChange]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Mark field as touched and validate
    setTouched(prev => ({ ...prev, [field]: true }));
    validateField(field, value);
    scheduleFieldTrack(field, value);
  };

  const scheduleFieldTrack = (field, value) => {
    if (trackTimersRef.current[field]) {
      clearTimeout(trackTimersRef.current[field]);
    }
    trackTimersRef.current[field] = setTimeout(() => {
      const trimmed = typeof value === "string" ? value.trim() : value;
      if (trimmed === "" || trimmed === null || typeof trimmed === "undefined") return;
      if (lastTrackedRef.current[field] === trimmed) return;
      lastTrackedRef.current[field] = trimmed;
      trackRegistrationField(field, trimmed, {
        source: "auto_register_form",
        step: 1,
        flow: formData.timeTrackingMethod || "unknown"
      });
    }, 700);
  };

  const validateField = (field, value) => {
    let validationResult = { isValid: true, error: null };

    switch (field) {
      case 'name':
        validationResult = validateTextField(value, 'Imię');
        break;
      case 'email':
        validationResult = validateEmail(value);
        break;
      case 'companyName':
        validationResult = validateTextField(value, 'Nazwa firmy');
        break;
      default:
        break;
    }

    setErrors(prev => ({
      ...prev,
      [field]: validationResult.error
    }));

    // Notify parent component about validation change
    if (onValidationChangeRef.current) {
      const newErrors = { ...errors, [field]: validationResult.error };
      const hasErrors = Object.values(newErrors).some(error => error !== null);
      onValidationChangeRef.current(!hasErrors && acceptedTerms);
    }
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    validateField(field, formData[field]);
    trackRegistrationField(field, formData[field], {
      source: "auto_register_form",
      step: 1,
      flow: formData.timeTrackingMethod || "unknown"
    });
  };

  const handleCheckboxChange = (e) => {
    const checked = e.target.checked;
    setFormData(prev => ({
      ...prev,
      acceptedTerms: checked
    }));

    if (onValidationChangeRef.current) {
      const hasErrors = Object.values(errors).some(error => error !== null);
      onValidationChangeRef.current(!hasErrors && checked);
    }
  };

  const handleTermsLinkClick = (site) => (e) => {
    e.preventDefault();
    window.open('/' + site, '_blank');
  };

  useEffect(() => {
    return () => {
      Object.values(trackTimersRef.current).forEach((timer) => clearTimeout(timer));
    };
  }, []);

  // Validate all fields when component mounts or formData changes
  useEffect(() => {
    if (content) {
      const nameValidation = validateTextField(name, 'Imię');
      const emailValidation = validateEmail(email);
      const companyNameValidation = validateTextField(companyName, 'Nazwa firmy');

      const newErrors = {
        name: nameValidation.error,
        email: emailValidation.error,
        companyName: companyNameValidation.error
      };

      setErrors(newErrors);

      // Notify parent about initial validation state
      if (onValidationChangeRef.current) {
        const hasErrors = Object.values(newErrors).some(error => error !== null);
        onValidationChangeRef.current(!hasErrors && acceptedTerms);
      }
    }
  }, [content]);

  // Update validation when formData changes
  useEffect(() => {
    if (content && onValidationChangeRef.current) {
      const nameValidation = validateTextField(name, 'Imię');
      const emailValidation = validateEmail(email);
      const companyNameValidation = validateTextField(companyName, 'Nazwa firmy');

      const newErrors = {
        name: nameValidation.error,
        email: emailValidation.error,
        companyName: companyNameValidation.error
      };

      const hasErrors = Object.values(newErrors).some(error => error !== null);
      onValidationChangeRef.current(!hasErrors && acceptedTerms);
    }
  }, [name, email, companyName, acceptedTerms, content]);

  const getInputStyle = (field) => {
    const baseStyle = { ...styles.input };
    if (touched[field] && errors[field]) {
      baseStyle.border = "1px solid #ff4444";
    } else if (touched[field] && !errors[field] && formData[field]?.trim()) {
      baseStyle.border = "1px solid #4CAF50";
    }
    return baseStyle;
  };

  return (
    <form style={styles.form}>
      <div style={styles.inputContainer}>
        <div style={styles.label}>
          {content.fields.name.label}
          {content.fields.name.required && <span style={styles.asterisk}>*</span>}
        </div>
        <input
          type="text"
          placeholder={content.fields.name.placeholder}
          id="name-input"
          value={name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          onBlur={() => handleBlur('name')}
          required={content.fields.name.required}
          style={getInputStyle('name')}
        />
        {touched.name && errors.name && (
          <div style={styles.errorText}>{errors.name}</div>
        )}
      </div>
      <div style={styles.inputContainer}>
        <div style={styles.label}>
          {content.fields.surname.label}
          {content.fields.surname.required && <span style={styles.asterisk}>*</span>}
        </div>
        <input
          type="text"
          placeholder={content.fields.surname.placeholder}
          id="surname-input"
          value={surname}
          onChange={(e) => handleInputChange('surname', e.target.value)}
          onBlur={() => handleBlur('surname')}
          required={content.fields.surname.required}
          style={getInputStyle('surname')}
        />
        {touched.surname && errors.surname && (
          <div style={styles.errorText}>{errors.surname}</div>
        )}
      </div>
      <div style={styles.inputContainer}>
        <div style={styles.label}>
          {content.fields.email.label}
          {content.fields.email.required && <span style={styles.asterisk}>*</span>}
        </div>
        <input
          type="email"
          placeholder={content.fields.email.placeholder}
          id="email-input"
          value={email}
          onChange={(e) => handleInputChange('email', e.target.value)}
          onBlur={() => handleBlur('email')}
          required={content.fields.email.required}
          style={getInputStyle('email')}
        />
        {touched.email && errors.email && (
          <div style={styles.errorText}>{errors.email}</div>
        )}
      </div>
      {content.fields.companyName && (
        <div style={styles.inputContainer}>
          <div style={styles.label}>
            {content.fields.companyName.label}
            {content.fields.companyName.required && <span style={styles.asterisk}>*</span>}
          </div>
          <input
            type="text"
            placeholder={content.fields.companyName.placeholder}
            id="company-name-input"
            value={companyName}
            onChange={(e) => handleInputChange('companyName', e.target.value)}
            onBlur={() => handleBlur('companyName')}
            required={content.fields.companyName.required}
            style={getInputStyle('companyName')}
          />
          {touched.companyName && errors.companyName && (
            <div style={styles.errorText}>{errors.companyName}</div>
          )}
        </div>
      )}
      {content.terms && (
        <div style={styles.checkboxContainer}>
          <input
            type="checkbox"
            checked={acceptedTerms || false}
            onChange={handleCheckboxChange}
            style={styles.checkbox}
          />
          <span style={styles.checkboxText}>
            {content.terms.iAccept}{" "}
            <a
              href="/regulamin"
              onClick={handleTermsLinkClick('regulamin')}
              style={hoveredLink === 'terms' ? styles.linkHover : styles.link}
              onMouseEnter={() => setHoveredLink('terms')}
              onMouseLeave={() => setHoveredLink(null)}
            >
              {content.terms.termsOfServiceLinkText}
            </a>
            {" " + content.terms.and + " "}
            <a
              href="/privacy"
              onClick={handleTermsLinkClick('privacy')}
              style={hoveredLink === 'privacy' ? styles.linkHover : styles.link}
              onMouseEnter={() => setHoveredLink('privacy')}
              onMouseLeave={() => setHoveredLink(null)}
            >
              {content.terms.privacyPolicyLinkText}
            </a>
          </span>
        </div>
      )}
    </form>
  );
};

export default StepOne;
